import { useEffect, useState } from "react";
import "./SubNav.scss";
import BellIcons from "../../../Assets/Img/BellIcon";
import { Link, useNavigate } from "react-router-dom";
import CscSubLogo from "Assets/Img/CscSubLogo";
import Button from "Components/Common/Button/Button";
import useLogout from "../../../hooks/useLogout";
import { getUnreadCount } from "../../../services/notificationService";
import useRefetchOnFocus from "../../../hooks/useRefetchOnFocus";
import useSwitchBackToAdmin from "../../../hooks/useSwitchBackToAdmin";
import { isImpersonating } from "../../../utils/Common/impersonation";

interface Crumb {
  label: string;
  link?: string;
  onClick?: () => void;
}

interface SubNavProps {
  crumbs?: Crumb[];
  userName?: string;
  userInitials?: string;
  role?: string;
  className?: string;
  crumbClass?: string;
  logoClass?: string;
  notificationCount?: number;
  notifications?: boolean;
  showSignout?: boolean;
  toggleDark?: () => void;
  darkMode?: boolean;
}

const SubNav = ({
  crumbs = [],
  userName,
  userInitials,
  role,
  className = "subNav",
  crumbClass = "crumbs",
  notificationCount = 0,
  notifications = false,
  showSignout = true,
  toggleDark,
  darkMode = false,
}: SubNavProps) => {
  const { handleLogout, loggingOut } = useLogout();
  const { switchBackToAdmin } = useSwitchBackToAdmin();
  const navigate = useNavigate();
  const [showBackToAdmin] = useState(isImpersonating());
  const [unreadCount, setUnreadCount] = useState(0);
  const [countLoading, setCountLoading] = useState(notifications);
  const [userAvatar, setUserAvatar] = useState<string>(
    JSON.parse(localStorage.getItem("user") || "{}").avatar?.url ?? "",
  );

  useEffect(() => {
    const handler = () => {
      const u = JSON.parse(localStorage.getItem("user") || "{}");
      setUserAvatar(u.avatar?.url ?? "");
    };
    window.addEventListener("avatar-changed", handler);
    return () => window.removeEventListener("avatar-changed", handler);
  }, []);
  const notifPath = (() => {
    const userRole = JSON.parse(localStorage.getItem("user") || "{}").role;
    if (userRole === "owner") return "/owner-notifications";
    if (userRole === "lender") return "/lender-notifications";
    if (userRole === "gc") return "/gc-notifications";
    if (userRole === "inspector") return "/inspector-notifications";
    return "/notifications";
  })();

  const fetchCount = async () => {
    if (!notifications) return;
    setCountLoading(true);
    try {
      const res: any = await getUnreadCount();
      const count = res?.data?.data?.count ?? 0;
      setUnreadCount(count);
    } catch {
    } finally {
      setCountLoading(false);
    }
  };

  useRefetchOnFocus(fetchCount);
  useEffect(() => {
    fetchCount();
    const handler = () => {
      fetchCount();
    };
    window.addEventListener("notification-unread-count-changed", handler);
    return () =>
      window.removeEventListener("notification-unread-count-changed", handler);
  }, []);

  return (
    <nav className={className}>
      <div className="subNavLogo" onClick={() => navigate("/gc-dashboard")}>
        <CscSubLogo />
      </div>

      <div className="navDiv" />

      <div className={crumbClass}>
        {crumbs.map((item, index) => (
          <span key={index}>
            {item.link ? (
              <Link to={item.link}>{item.label}</Link>
            ) : item.onClick ? (
              <Link
                to="#"
                onClick={(e) => {
                  e.preventDefault();
                  item.onClick!();
                }}
              >
                {item.label}
              </Link>
            ) : (
              <strong>{item.label}</strong>
            )}
            {index !== crumbs.length - 1 && " › "}
          </span>
        ))}
      </div>

      <div className="navRight">
        {/* {toggleDark && (
          <Button onClick={toggleDark} className="themeBtn">
            {darkMode ? "Light Mode" : " Dark Mode"}
          </Button>
        )} */}

        {notifications && (
          <div className="notifySec">
            <div
              className="bell"
              onClick={() => navigate(notifPath)}
              style={{ cursor: "pointer" }}
            >
              <BellIcons />
              {countLoading ? (
                <div className="bellbadgeWrapper">
                  <div className="bellbadge">
                    <span className="btn-spinner" />
                  </div>
                </div>
              ) : (notificationCount || unreadCount) > 0 ? (
                <div className="bellbadgeWrapper">
                  <div className="bellbadge">
                    {(notificationCount || unreadCount) > 9
                      ? "9+"
                      : notificationCount || unreadCount}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        )}

        {role && <span className="navPill">{role}</span>}

        <div
          className="navAvatar"
          onClick={() => navigate("/profile")}
          style={{ cursor: "pointer" }}
          title="My Profile"
        >
          {userAvatar ? (
            <img
              src={userAvatar}
              alt={userInitials}
              className="navAvatar__img"
            />
          ) : (
            userInitials
          )}
        </div>

        <span className="navUserName">{userName}</span>

        {showBackToAdmin && (
          <Button className="backToAdminBtn" onClick={switchBackToAdmin}>
            Back to Admin
          </Button>
        )}

        {showSignout && (
          <>
            <Button
              className="signoutBtn"
              onClick={handleLogout}
              disabled={loggingOut}
            >
              {loggingOut && <span className="btn-spinner" />}{" "}
              {loggingOut ? "Signing out" : "Sign Out"}
            </Button>
          </>
        )}
      </div>
    </nav>
  );
};

export default SubNav;
